// src/api/pitchService.js - Updated with refreshReplyStatus
import { getCookieValue } from '../api/cookie';

const API_URL = process.env.REACT_APP_API_URL || "";

/** Minimal helpers for GET, POST, PATCH with JSON + CSRF */
async function getJson(endpoint) {
  const csrfToken = getCookieValue('csrf_token');
  const res = await fetch(`${API_URL}${endpoint}`, {
    credentials: 'include',
    headers: { 'X-CSRFToken': csrfToken },
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Request failed');
  return data;
}

async function postJson(endpoint, payload = {}) {
  const csrfToken = getCookieValue('csrf_token');
  const res = await fetch(`${API_URL}${endpoint}`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRFToken': csrfToken,
    },
    body: JSON.stringify(payload),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Request failed');
  return data;
}

async function patchJson(endpoint, payload = {}) {
  const csrfToken = getCookieValue('csrf_token');
  const res = await fetch(`${API_URL}${endpoint}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRFToken': csrfToken,
    },
    body: JSON.stringify(payload),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Request failed');
  return data;
}

async function deleteJson(endpoint) {
  const csrfToken = getCookieValue('csrf_token');
  const res = await fetch(`${API_URL}${endpoint}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: {
      'X-CSRFToken': csrfToken,
    },
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Request failed');
  return data;
}

// ========== 1) Fetch DISTINCT days_ago ==========
export function fetchDaysAgoList() {
  return getJson('/api/pitches/pending/days-ago');
}

// ========== 2) Fetch PITCHES based on days_ago ==========
export function fetchPitchesByDaysAgo(days) {
  return getJson(`/api/pitches?days_ago=${days}`);
}

// ========== 3) Fetch PITCHES by Company Name (global search) ==========
export function fetchPitchesByCompanyName(companyName) {
  return getJson(`/api/pitches?companyName=${encodeURIComponent(companyName)}`);
}

// ========== 4) Update pitch status (PATCH example) - UPDATED TO ACCEPT REASON ==========
export function updatePitchStatus(pitchId, updateData) {
  return patchJson(`/api/pitches/${pitchId}`, updateData);
}

/** 
 * Fetch Pitch History
 * @param {Object} options - Parameters for fetching history.
 * @param {number} options.page
 * @param {number} options.limit
 * @param {string} [options.companyName]
 * @param {string} [options.startDate]
 * @param {string} [options.endDate]
 */
export function fetchPitchHistory({ page, limit, companyName, startDate, endDate }) {
  const params = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
  });
  if (companyName) {
    params.set('companyName', companyName);
  }
  if (startDate) {
    params.set('startDate', startDate);
  }
  if (endDate) {
    params.set('endDate', endDate);
  }
  return getJson(`/api/pitches/history?${params.toString()}`);
}

// ========== 5) Refresh email reply status ==========
export function refreshReplyStatus() {
  return postJson('/api/pitches/refresh-replies');
}


/**
 * Send a follow-up email for a pitch
 * @param {number} pitchId - The ID of the pitch to follow up
 * @param {boolean} forceFollowUp - Whether to force follow-up even if there's a reply
 * @returns {Promise} API response
 */
export function sendPitchFollowUp(pitchId, forceFollowUp = false) {
  return postJson(`/api/pitches/${pitchId}/follow-up`, { force: forceFollowUp });
}

/**
 * Delete a pitch, with option to restore the declaration
 * @param {number} pitchId - The ID of the pitch to delete
 * @param {boolean} restoreDeclaration - Whether to restore the declaration for new pitches
 * @returns {Promise} API response
 */
export function deletePitch(pitchId, restoreDeclaration = false) {
  return deleteJson(`/api/pitches/${pitchId}?restore_declaration=${restoreDeclaration}`);
}